import React, { useState } from 'react';
import { Field, Form } from 'react-final-form';
import moment from 'moment';
import Bluebird from 'bluebird';
import { Grid, Box, Button, Typography } from '@mui/material';
import { confirmAlert } from 'react-confirm-alert';
import ButtonWithLoading from '../../../../components/ButtonWithLoading';
import { FormContainer, GridRow, RowContent, RowTitle } from '../FormGrid';
import MinMaxTimePicker from '../../../../components/MinMaxTimePicker';
import RescuerField from './RescuerField';
import ReceivingAgencyField from './ReceivingAgencyField';
import snackbarHelper from '../../../../helpers/snackbarHelper';
import { getUserFullName } from '../../../../helpers/user';
import { Colors } from '../../../../assets/theme/Colors';
import { formatPhoneNumber } from '../../../../helpers/formatters';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import NotesTextEditor from '../../../../components/NotesTextEditor';
import { BaseCard } from '../../../../components/Common/BaseCard';
import * as rescuesApi from '../../../../api/rescues';
import DonorLinkLoggedItemsMessage from './DonorLinkLoggedItemsMessage';

const DONOR_LINK_LOGGED_ITEMS_MOVE = 'MOVE';
const DONOR_LINK_LOGGED_ITEMS_DISCARD = 'DISCARD';

const timePickerSx = {
  '& input': {
    textAlign: 'center',
  },
};

const validate = (values) => {
  const errors = {};

  if (!values.receiver_id) {
    errors.receiver = 'Required';
  }

  return errors;
};

const RescueEditAdminForm = ({
  user,
  rescue,
  isMobileView,
  rescuersList = [],
  receivingAgenciesList = [],
  rescuerSubstitutionsList = [],
  updateRescue,
}) => {
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const onAssignClick = (rescuer, form) => {
    if (rescuer.id && rescuer.name) {
      form.batch(() => {
        if (rescue.adopter_id && rescue.rescuer_id === null) {
          form.change('adopter_id', null);
        }
        form.change('rescuer_id', rescuer.id);
        form.change('rescuer', rescuer.name);
      });
    }
  };

  const toggleCancel = (form, values, donorLinkLoggedItemsAction = null) =>
    form.batch(() => {
      form.change('cancelled_by_id', values.cancelled_by_id ? null : user.id);
      form.change('canceller', values.cancelled_by_id ? null : getUserFullName(user));
      form.change('donor_link_logged_items_action', values.cancelled_by_id ? null : donorLinkLoggedItemsAction);
    });

  // the donor already logged food in Toast for this rescue, let the SD move it to the next pickup or drop it
  const cancelWithDonorLinkLoggedItems = async (form, values) => {
    let loggedItems;

    try {
      const response = await rescuesApi.getRescueDonorLinkLoggedItems(rescue.id);
      const json = await response.json();

      loggedItems = json.data;
    } catch (err) {
      return snackbarHelper.error(err.message);
    }

    if (!loggedItems) {
      return toggleCancel(form, values);
    }

    const nextPickup = loggedItems.next_pickup;
    const buttons = [
      {
        label: 'Back',
        color: 'secondary',
      },
      {
        label: nextPickup ? 'No, discard items' : 'Discard items',
        color: nextPickup ? 'secondary' : 'primary',
        onClick: () => toggleCancel(form, values, DONOR_LINK_LOGGED_ITEMS_DISCARD),
      },
    ];

    if (nextPickup) {
      buttons.push({
        label: 'Yes, move items',
        color: 'primary',
        onClick: () => toggleCancel(form, values, DONOR_LINK_LOGGED_ITEMS_MOVE),
      });
    }

    const renderDialog = ({ onClose }) => (
      <ConfirmationDialog
        title="Cancel Rescue"
        buttons={buttons}
        closeDialog={onClose}
        customMessage={<DonorLinkLoggedItemsMessage items={loggedItems.items} nextPickup={nextPickup} />}
      />
    );

    return confirmAlert({ customUI: renderDialog });
  };

  const handleSubmit = values => Bluebird
    .try(() => setIsFormSubmitting(true))
      .then(() => updateRescue(values))
    .catch(err => {
        snackbarHelper.error(err.message);
        setIsFormSubmitting(false);
      });

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{
        receiver_id: rescue.receiver_id,
        receiver: rescue.receiver,
        pickup_begin: rescue.pickup_begin,
        pickup_end: rescue.pickup_end,
        rescuer_notes: rescue.rescuer_notes || '',
        rescuer_id: rescue.rescuer_id,
        rescuer: rescue.rescuer,
        adopter_id: rescue.adopter_id,
        adopter: rescue.adopter,
        released_by_id: rescue.released_by_id,
        releaser: rescue.releaser,
        cancelled_by_id: rescue.cancelled_by_id,
        canceller: rescue.canceller,
      }}
      validate={validate}
      render={({ handleSubmit, values, form, pristine, dirty, invalid }) => {
        const isAdopted = values.adopter && values.rescuer_id === null && values.released_by_id === null;
        const selectedRescuer = isAdopted
          ? rescuersList.find((rescuer) => rescuer.id === values.adopter_id)
          : rescuersList.find((rescuer) => rescuer.id === values.rescuer_id);

        return (
          <form onSubmit={handleSubmit} data-testid="receiver-location-add-form">
            <Field name="canceller" render={() => null} />

            <Field name="donor_link_logged_items_action" render={() => null} />

            <Field name="released_by_id" render={() => null} />

            <Box
              borderColor={rescue.cancelled_by_id !== null ? 'error.main' : 'text.primary'}
              borderRadius="4px"
              border={1}
              p={2}
            >
              <Box
                sx={(theme) => ({
                  margin: -2,
                  border: '4px',
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                  marginBottom: 1,
                  padding: theme.spacing(0.5, 1, 0.5, 1),
                  color: Colors.white,
                  backgroundColor: theme.palette.error.main,
                })}
                display={rescue.cancelled_by_id !== null ? 'block' : 'none'}
              >
                <Typography variant="subtitle2">Rescue cancelled!</Typography>
              </Box>

              <FormContainer>
                <GridRow>
                  <RowTitle>
                    Receiving Agency:
                  </RowTitle>

                  <RowContent>
                    <ReceivingAgencyField form={form} receivingAgenciesList={receivingAgenciesList} />
                  </RowContent>
                </GridRow>

                <GridRow>
                  <RowTitle>
                    Pickup Window:
                  </RowTitle>

                  <Box alignItems="center" display="flex">
                    <Box>
                      <Field
                        label="When"
                        name="pickup_begin"
                        render={({ input }) => (
                          <MinMaxTimePicker
                            sx={timePickerSx}
                            name={input.name}
                            value={moment(input.value, 'HH:mm')}
                            maxTime={moment(values.pickup_end, 'HH:mm')}
                            onTimeChange={({ event, duration }) => {
                              const endTime = event.clone().add(duration);

                              form.batch(() => {
                                form.change('pickup_begin', event.format('HH:mm:ss'));
                                form.change('pickup_end', endTime.format('HH:mm:ss'));
                              });
                            }}
                          />
                        )}
                      />
                    </Box>

                    <Box mx={1}>
                      -
                    </Box>

                    <Box>
                      <Field
                        InputProps={{
                          textAlign: 'center',
                        }}
                        name="pickup_end"
                        render={({ input }) => (
                          <MinMaxTimePicker
                            showFieldErrors
                            sx={timePickerSx}
                            name={input.name}
                            value={moment(input.value, 'HH:mm')}
                            minTime={moment(values.pickup_begin, 'HH:mm:ss')}
                            onTimeChange={({ event, isValid }) => {
                              if (isValid) {
                                form.change('pickup_end', event.format('HH:mm:ss'));
                              }
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </GridRow>

                <GridRow>
                  <RowTitle>
                    Rescuer Notes:
                  </RowTitle>

                  <Grid item xs={12}>
                    <Field
                      name="rescuer_notes"
                      render={({ input }) => (
                        <NotesTextEditor
                          value={input.value}
                          onTextChange={(value) => {
                            input.onChange(value);
                          }}
                          styles={{
                            '& .ql-editor': {
                              width: '100%',
                              wordBreak: 'break-word',
                              minHeight: '100px',
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </GridRow>

                <GridRow>
                  <RowTitle>
                    Rescuer:
                  </RowTitle>

                  <RowContent>
                    <RescuerField form={form} rescuersList={rescuersList} />
                  </RowContent>
                </GridRow>

                {selectedRescuer && (
                  <GridRow>
                    <RowTitle>Rescuer Contact:</RowTitle>

                    {selectedRescuer?.phone && (
                      <RowContent>
                        <span>Phone: </span>
                        <a href={`tel:${selectedRescuer.phone}`}>{formatPhoneNumber(selectedRescuer.phone)}</a>
                        {selectedRescuer.phone_ext && <span>{` (ext: ${selectedRescuer.phone_ext})`}</span>}
                      </RowContent>
                    )}

                    {selectedRescuer?.email && (
                      <RowContent>
                        Email: <a href={`mailto:${selectedRescuer.email}`}>{selectedRescuer.email}</a>
                      </RowContent>
                    )}
                  </GridRow>
                )}

                <Field name="adopter" render={() => null} />

                {rescuerSubstitutionsList.length > 0 && (
                  <GridRow>
                    <RowTitle>Recommended Substitutes:</RowTitle>

                    <RowContent>
                      {rescuerSubstitutionsList.map((subsititution) => (
                        <Box key={subsititution.id} my={1}>
                          <BaseCard sx={{ borderRadius: '5px', padding: 2 }}>
                            <Box>
                              <Typography variant="body1">
                                <b>{subsititution.name}</b>
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="body1">
                                {subsititution.phone && `${formatPhoneNumber(subsititution.phone)}`}
                                {subsititution.phone_ext && <span>{` (ext: ${subsititution.phone_ext})`}</span>}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="body1">{subsititution.email}</Typography>
                            </Box>
                            <Box pt={1}>
                              <Button
                                disabled={
                                  values.rescuer_id === subsititution.id || values.adopter_id === subsititution.id
                                }
                                onClick={() => onAssignClick(subsititution, form)}
                                variant="contained"
                                color="primary"
                              >
                                Assign
                              </Button>
                            </Box>
                          </BaseCard>
                        </Box>
                      ))}
                    </RowContent>
                  </GridRow>
                )}

                <GridRow>
                  <Grid item>
                    <Box
                      alignItems="center"
                      display="flex"
                      flexWrap="wrap"
                      sx={(theme) => ({
                        '& > :not(:first-child)': {
                          [theme.breakpoints.only('xs')]: {
                            marginTop: theme.spacing(1),
                            marginBottom: theme.spacing(1),
                          },
                          [theme.breakpoints.up('sm')]: {
                            marginLeft: theme.spacing(1),
                            marginRight: theme.spacing(1),
                          },
                        },
                      })}
                    >
                      <ButtonWithLoading
                        disabled={pristine || !dirty || isFormSubmitting || invalid}
                        fullWidth={isMobileView}
                        color="primary"
                        type="submit"
                        isLoading={isFormSubmitting}
                        data-testid="rescue-edit-admin-save"
                      >
                        <Box sx={{ color: Colors.white }}>Update</Box>
                      </ButtonWithLoading>

                      <Button
                        disabled={!!rescue.donor_deleted_at}
                        fullWidth={isMobileView}
                        color="secondary"
                        variant="contained"
                        onClick={() => {
                          if (rescue.rescue_confirmed && !values.cancelled_by_id) {
                            return cancelWithDonorLinkLoggedItems(form, values);
                          }

                          return toggleCancel(form, values);
                        }}
                      >
                        {values.cancelled_by_id ? 'Un-cancel Rescue' : 'Cancel Rescue'}
                      </Button>
                    </Box>
                  </Grid>
                </GridRow>
              </FormContainer>
            </Box>
          </form>
        );
      }}
    />
  );
};

export default RescueEditAdminForm;
